import {
  patchState,
  signalStore,
  withHooks,
  withMethods,
  withState,
} from '@ngrx/signals';
import { BaseState, withCrudOperations } from '../crud.state';
import { withIndicatorDataSelectors } from './indicator-data.selectors';
import { IndicatorData } from '../../models/indicator-data';
import { IndicatorDataService } from '../../services/data/indicator-data.service';
import { inject } from '@angular/core';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { pipe, switchMap } from 'rxjs';
import { tapResponse } from '@ngrx/operators';
import { downloadBlob } from '../../utility/download';
import { ExportSheet } from '../../services/data/export-helper.service';
import { ActiveUserStore } from '../user/active-user.state';
import { withWidgetSelectors } from './indicator-data.widget-selectors';
import { withLogging } from '../features/logger.feature';
import { ConfirmationDialogService } from '../../services/confirmation-dialog.service';

export type IndicatorDataState = BaseState<IndicatorData>;

export const initialState: IndicatorDataState = {
  items: {},
  loading: false,
  selectedId: '',
  selection: [],
  initialized: false,
  itemsLoaded: false,
  version: 0,
  lastPersistedId: '',
  trigger: undefined,
  events: {
    update: ['runBookmarkValidation'],
  },
  entityType: 'IndicatorData',
  entityLabel: 'Daten',
  filter: {
    query: '',
    category: [],
    tags: undefined,
    products: undefined,
    published: null,
    locked: null,
    ids: undefined,
  },
};

export const IndicatorDataStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withCrudOperations<IndicatorData>(IndicatorDataService),
  withLogging(initialState.entityType),
  withIndicatorDataSelectors(),
  withWidgetSelectors(),
  withMethods(
    (
      store,
      indicatorDataService = inject(IndicatorDataService),
      confirmationDialogService = inject(ConfirmationDialogService)
    ) => ({
      //findMany: rxMethod<{ dataLayerIds: string[]; indicatorIds: string[] }>(
      findMany: rxMethod<{
        dataLayerIds: string[];
        categoryIds: string[];
        filterByKeys?: string[];
      }>(
        pipe(
          switchMap(({ dataLayerIds, categoryIds, filterByKeys }) => {
            return indicatorDataService
              .findMany(dataLayerIds, categoryIds, filterByKeys)
              .pipe(
                tapResponse({
                  next: response => {
                    patchState(store, {
                      items: response,
                      loading: false,
                      initialized: true,
                      itemsLoaded: true,
                    });
                  },
                  error: (error: any) => {
                    patchState(store, {
                      loading: false,
                      initialized: true,
                      error,
                    });
                    confirmationDialogService.addErrorMessage(
                      `${store.entityLabel()} konnten nicht geladen werden.`,
                      'Daten konnten nicht geladen werden'
                    );
                  },
                })
              );
          })
        )
      ),
      fetchByIndicatorIds: rxMethod<{ indicatorIds: string[] }>(
        pipe(
          switchMap(({ indicatorIds }) => {
            return indicatorDataService.findByIndicatorIds(indicatorIds).pipe(
              tapResponse({
                next: response => {
                  patchState(store, {
                    items: response,
                    loading: false,
                    initialized: true,
                    itemsLoaded: true,
                  });
                },
                error: (error: any) => {
                  patchState(store, {
                    loading: false,
                    initialized: true,
                    error,
                  });
                  confirmationDialogService.addErrorMessage(
                    `${store.entityLabel()} konnten nicht geladen werden.`,
                    'Daten konnten nicht geladen werden'
                  );
                },
              })
            );
          })
        )
      ),
      convertToXlsx: rxMethod<{ exportData: ExportSheet[]; filename: string }>(
        pipe(
          switchMap(({ exportData, filename }) => {
            return indicatorDataService
              .convertToXlsx(exportData, filename)
              .pipe(
                tapResponse({
                  next: blob => {
                    patchState(store, {
                      selection: [],
                    });
                    downloadBlob(blob, `Daten-Export_${filename}.xlsx`);
                  },
                  error: console.error,
                  //finalize: () => patchState(store, { loading: false }),
                })
              );
          })
        )
      ),
      convertChartDataToXlsx: rxMethod<{
        exportData: ExportSheet[];
        filename: string;
        source: string;
      }>(
        pipe(
          switchMap(({ exportData, filename, source }) => {
            return indicatorDataService
              .convertChartDataToXlsx(exportData, filename, source)
              .pipe(
                tapResponse({
                  next: blob => {
                    downloadBlob(blob, `${filename}.xlsx`);
                  },
                  error: console.error,
                })
              );
          })
        )
      ),
    })
  ),
  withHooks({
    onInit(store, activeUserStore = inject(ActiveUserStore)) {
      if (
        (activeUserStore.isAdmin() &&
          activeUserStore.context() !== 'atlasInternal') ||
        (activeUserStore.canSeeAllInWorkspaceType('indicatorCategory') &&
          activeUserStore.canSeeAllInWorkspaceType('dataLayerType') &&
          activeUserStore.context() === 'dataBackend')
      ) {
        store.loadAllItemsByPromise();
      }
    },
    onDestroy() {
      console.log('on destroy');
    },
  })
);
